/**
 * Recálculo de consideraciones previas (ajustes de conceptos).
 * Solo se persisten conceptos cambiados; no se reenvía la plantilla completa.
 */
import {
  getMontoDesdeAjuste,
  getMontoDesdeAjusteParaCanal,
  getDestinoDesdeAjuste,
  parseAjusteTemporal,
  resolverMontosPorDestino,
} from './ajusteConceptoDestino';

export function plameIdDeConcepto(concepto) {
  const id =
    concepto?.plame_configuracion_id
    ?? concepto?.plame_configuracion?.id
    ?? concepto?.plameConfiguracion?.id
    ?? concepto?.id_concepto
    ?? null;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

export function codigoDeConcepto(concepto) {
  return String(
    concepto?.codigo
    || concepto?.codigoPlame
    || concepto?.codigo_plame
    || concepto?.plame_configuracion?.codigo_plame
    || concepto?.plameConfiguracion?.codigo_plame
    || ''
  );
}

export function clavesDeConcepto(concepto) {
  const keys = [];
  if (concepto == null) return keys;
  if (concepto.id != null && concepto.id !== '') keys.push(String(concepto.id));
  const plameId = plameIdDeConcepto(concepto);
  if (plameId != null) keys.push(String(plameId));
  const codigo = codigoDeConcepto(concepto);
  if (codigo) keys.push(codigo);
  return [...new Set(keys)];
}

export function buscarValorPorConcepto(mapa, concepto) {
  if (!mapa || typeof mapa !== 'object') return undefined;
  for (const k of clavesDeConcepto(concepto)) {
    if (mapa[k] !== undefined) return mapa[k];
  }
  return undefined;
}

function tipoBucket(tipo) {
  const t = String(tipo || '').toLowerCase();
  if (t === 'descuento' || t === 'descuentos') return 'descuentos';
  if (t === 'aporte' || t === 'aportes') return 'aportes';
  return 'ingresos';
}

function tipoSingular(tipo) {
  const t = String(tipo || '').toLowerCase();
  if (t === 'descuento' || t === 'descuentos') return 'descuento';
  if (t === 'aporte' || t === 'aportes') return 'aporte';
  return 'ingreso';
}

function esFilaConcepto(c) {
  if (!c || typeof c !== 'object') return false;
  return Boolean(
    plameIdDeConcepto(c)
    || codigoDeConcepto(c)
    || c.tipo_concepto
    || c.plame_configuracion
  );
}

export function colapsarPorPlameId(lista = []) {
  const porPlame = new Map();
  (lista || []).forEach((c) => {
    const plameId = plameIdDeConcepto(c);
    const key = plameId != null
      ? `id:${plameId}`
      : `cod:${codigoDeConcepto(c) || `row:${porPlame.size}`}`;
    const prev = porPlame.get(key);
    if (!prev) {
      porPlame.set(key, { ...c, plame_configuracion_id: plameId ?? c.plame_configuracion_id });
      return;
    }
    const prevMonto = Number(prev.monto) || 0;
    const currMonto = Number(c.monto) || 0;
    porPlame.set(key, {
      ...prev,
      ...c,
      monto: currMonto > prevMonto ? c.monto : prev.monto,
      monto_interna: c.monto_interna ?? prev.monto_interna,
      destino_ajuste: mergeDestino(prev.destino_ajuste, c.destino_ajuste),
      plame_configuracion: prev.plame_configuracion || c.plame_configuracion,
      plame_configuracion_id: plameId ?? prev.plame_configuracion_id,
      tipo_concepto: prev.tipo_concepto || c.tipo_concepto,
    });
  });
  return [...porPlame.values()];
}

export function agruparConceptos(conceptos) {
  const vacio = { ingresos: [], descuentos: [], aportes: [] };
  if (!conceptos) return vacio;

  const push = (bucket, c) => {
    if (!esFilaConcepto(c)) return;
    bucket.push(c);
  };

  if (Array.isArray(conceptos)) {
    const out = { ingresos: [], descuentos: [], aportes: [] };
    conceptos.forEach((c) => push(out[tipoBucket(c.tipo_concepto)], c));
    return {
      ingresos: colapsarPorPlameId(out.ingresos),
      descuentos: colapsarPorPlameId(out.descuentos),
      aportes: colapsarPorPlameId(out.aportes),
    };
  }

  const out = {
    ingresos: [...(Array.isArray(conceptos.ingresos) ? conceptos.ingresos : [])],
    descuentos: [...(Array.isArray(conceptos.descuentos) ? conceptos.descuentos : [])],
    aportes: [...(Array.isArray(conceptos.aportes) ? conceptos.aportes : [])],
  };

  // Recuperar spread de array → objeto ({0: fila, 1: fila, ingresos: [...]})
  Object.keys(conceptos).forEach((k) => {
    if (k === 'ingresos' || k === 'descuentos' || k === 'aportes') return;
    push(out[tipoBucket(conceptos[k]?.tipo_concepto)], conceptos[k]);
  });

  return {
    ingresos: colapsarPorPlameId(out.ingresos),
    descuentos: colapsarPorPlameId(out.descuentos),
    aportes: colapsarPorPlameId(out.aportes),
  };
}

export function flattenConceptos(conceptos) {
  const g = agruparConceptos(conceptos);
  return [...g.ingresos, ...g.descuentos, ...g.aportes];
}

export function mergeDestino(a, b) {
  const x = String(a || '').toLowerCase() || 'plame';
  const y = String(b || '').toLowerCase() || 'plame';
  if (!a) return y;
  if (!b) return x;
  if (x === y) return x;
  if (x === 'ambas' || y === 'ambas') return 'ambas';
  if ((x === 'plame' && y === 'interna') || (x === 'interna' && y === 'plame')) return 'ambas';
  return y;
}

export function normalizarConceptoParaGuardar(concepto, tipoConceptoFallback = null) {
  const plameId = plameIdDeConcepto(concepto);
  if (!plameId) return null;

  const tipo = tipoSingular(concepto.tipo_concepto || tipoConceptoFallback || 'ingreso');
  const destino =
    concepto.destino_ajuste
    || (String(concepto.tipo_boleta || '').toLowerCase() === 'interna' ? 'interna' : null)
    || 'plame';

  const monto = parseFloat(concepto.monto ?? 0);
  let montoInterna = concepto.monto_interna;
  if (montoInterna === undefined || montoInterna === null) {
    if (String(concepto.tipo_boleta || '').toLowerCase() === 'interna' || destino === 'interna') {
      montoInterna = monto;
    } else {
      montoInterna = null;
    }
  } else {
    montoInterna = parseFloat(montoInterna);
  }

  return {
    plame_configuracion_id: Number(plameId),
    tipo_concepto: tipo,
    monto: Number.isFinite(monto) ? monto : 0,
    destino_ajuste: destino,
    monto_interna: montoInterna != null && Number.isFinite(montoInterna) ? montoInterna : null,
  };
}

/**
 * Ajustes (Jatsa / consideraciones) se añaden sobre la ficha.
 * No se borra un concepto de ficha solo porque el ajuste traiga otro código.
 * Si el mismo PLAME está en ambos, gana el ajuste.
 */
export function fusionarConceptosFichaConAjustes(ficha, ajustes) {
  const base = agruparConceptos(ficha);
  const extra = agruparConceptos(ajustes);

  const clave = (c) => {
    const id = plameIdDeConcepto(c);
    if (id != null) return `id:${id}`;
    const codigo = codigoDeConcepto(c);
    return codigo ? `cod:${codigo}` : null;
  };

  const mergeBucket = (baseList, ajusteList) => {
    const map = new Map();
    (baseList || []).forEach((c) => {
      const key = clave(c);
      if (!key) return;
      map.set(key, { ...c });
    });
    (ajusteList || []).forEach((c) => {
      const key = clave(c);
      if (!key) return;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, { ...c });
        return;
      }
      map.set(key, {
        ...prev,
        ...c,
        plame_configuracion: c.plame_configuracion || prev.plame_configuracion,
        plame_configuracion_id: plameIdDeConcepto(c) ?? plameIdDeConcepto(prev),
        tipo_concepto: c.tipo_concepto || prev.tipo_concepto,
        monto: c.monto != null ? c.monto : prev.monto,
        monto_interna: c.monto_interna != null ? c.monto_interna : prev.monto_interna,
        destino_ajuste: c.destino_ajuste || prev.destino_ajuste,
        origen_jatsa: Object.prototype.hasOwnProperty.call(c, 'origen_jatsa')
          ? Boolean(c.origen_jatsa)
          : Boolean(prev.origen_jatsa),
      });
    });
    return colapsarPorPlameId([...map.values()]);
  };

  return {
    ingresos: mergeBucket(base.ingresos, extra.ingresos),
    descuentos: mergeBucket(base.descuentos, extra.descuentos),
    aportes: mergeBucket(base.aportes, extra.aportes),
  };
}

/**
 * Une PLAME + internos por plame_configuracion_id.
 * Nunca pisa un monto PLAME > 0 con 0 de una fila solo-interna.
 */
export function fusionarConceptosPlameEInternos(empleado) {
  const porPlameId = new Map();

  const upsert = (concepto, tipoFallback, esInterno = false) => {
    const normalizado = normalizarConceptoParaGuardar(concepto, tipoFallback);
    if (!normalizado) return;
    const key = String(normalizado.plame_configuracion_id);
    const prev = porPlameId.get(key);

    if (!prev) {
      if (esInterno) {
        porPlameId.set(key, {
          ...normalizado,
          monto: 0,
          monto_interna: normalizado.monto_interna ?? normalizado.monto,
          destino_ajuste: 'interna',
        });
      } else {
        porPlameId.set(key, normalizado);
      }
      return;
    }

    if (esInterno) {
      const montoInterna = normalizado.monto_interna ?? normalizado.monto;
      porPlameId.set(key, {
        ...prev,
        monto: Number(prev.monto) > 0 ? prev.monto : 0,
        monto_interna: montoInterna,
        destino_ajuste: mergeDestino(prev.destino_ajuste, 'interna'),
      });
      return;
    }

    const montoPlame = Number(normalizado.monto) > 0
      ? normalizado.monto
      : (Number(prev.monto) > 0 ? prev.monto : normalizado.monto);
    porPlameId.set(key, {
      ...prev,
      ...normalizado,
      monto: montoPlame,
      monto_interna: prev.monto_interna ?? normalizado.monto_interna,
      destino_ajuste: mergeDestino(prev.destino_ajuste, normalizado.destino_ajuste),
    });
  };

  const conceptos = empleado?.conceptos || [];
  if (Array.isArray(conceptos)) {
    conceptos.forEach((c) => {
      const esInterno = String(c.tipo_boleta || '').toLowerCase() === 'interna';
      upsert(c, c.tipo_concepto || null, esInterno);
    });
  } else {
    ['ingresos', 'descuentos', 'aportes'].forEach((tipo) => {
      (conceptos[tipo] || []).forEach((c) => {
        const tipoFallback =
          tipo === 'ingresos' ? 'ingreso' : tipo === 'descuentos' ? 'descuento' : 'aporte';
        const esInterno = String(c.tipo_boleta || '').toLowerCase() === 'interna';
        upsert(c, tipoFallback, esInterno);
      });
    });
  }

  const internos = Array.isArray(empleado?.conceptos_internos)
    ? empleado.conceptos_internos
    : [
        ...(empleado?.conceptos?.ingresosInternos || []),
        ...(empleado?.conceptos?.descuentosInternos || []),
        ...(empleado?.conceptos?.aportesInternos || []),
        ...(empleado?.conceptos_separados_internos?.ingresos || []),
        ...(empleado?.conceptos_separados_internos?.descuentos || []),
        ...(empleado?.conceptos_separados_internos?.aportes || []),
      ];

  internos.forEach((c) => {
    upsert({ ...c, tipo_boleta: c.tipo_boleta || 'interna' }, c.tipo_concepto || 'ingreso', true);
  });

  return [...porPlameId.values()];
}

function mapaEmpleado(mapas, empleadoId) {
  if (!mapas || typeof mapas !== 'object') return null;
  return mapas[empleadoId] || mapas[String(empleadoId)] || mapas[Number(empleadoId)] || null;
}

function setTieneEmpleado(setLike, empleadoId) {
  if (!setLike) return false;
  if (setLike instanceof Set) {
    return setLike.has(empleadoId) || setLike.has(String(empleadoId)) || setLike.has(Number(empleadoId));
  }
  if (Array.isArray(setLike)) {
    return setLike.some((x) => String(x) === String(empleadoId));
  }
  return false;
}

function empleadoTieneNuevos(empleado) {
  return flattenConceptos(empleado?.conceptos).some((c) => c.esNuevo);
}

function construirFilaAjuste(concepto, rawAjuste, defaultDestino, tipoConcepto) {
  const parsed = parseAjusteTemporal(rawAjuste);
  const destino = rawAjuste != null
    ? getDestinoDesdeAjuste(rawAjuste, defaultDestino)
    : (
      concepto.destino_ajuste
      || (String(concepto.tipo_boleta || '').toLowerCase() === 'interna' ? 'interna' : null)
      || defaultDestino
    );
  const tienePlameIndependiente = parsed && parsed.monto_plame !== undefined && parsed.monto_plame !== null && parsed.monto_plame !== '';
  const tieneInternaIndependiente = parsed && parsed.monto_interna !== undefined && parsed.monto_interna !== null && parsed.monto_interna !== '';
  if (tienePlameIndependiente && tieneInternaIndependiente) {
    const plameIdAmbos = plameIdDeConcepto(concepto);
    if (!plameIdAmbos) return null;
    return {
      plame_configuracion_id: plameIdAmbos,
      tipo_concepto: tipoSingular(concepto.tipo_concepto || tipoConcepto),
      monto: parseFloat(parsed.monto_plame || 0),
      destino_ajuste: 'ambas',
      monto_interna: parseFloat(parsed.monto_interna),
    };
  }
  const montoNuevo = rawAjuste != null
    ? getMontoDesdeAjusteParaCanal(rawAjuste, destino === 'interna' ? 'interna' : 'plame', concepto.monto_interna ?? concepto.monto)
    : (
      concepto.monto_interna != null && destino === 'interna'
        ? concepto.monto_interna
        : concepto.monto
    );
  const esFilaInterna = String(concepto.tipo_boleta || '').toLowerCase() === 'interna'
    && Number(concepto.monto) === 0
    && concepto.monto_interna != null;
  const resuelto = resolverMontosPorDestino({
    montoOriginal: esFilaInterna ? 0 : concepto.monto,
    montoNuevo,
    destino,
    montoInternaPrevio: tieneInternaIndependiente ? parsed.monto_interna : concepto.monto_interna,
  });
  if (tienePlameIndependiente) {
    resuelto.monto = Number(parsed.monto_plame);
  }
  if (tieneInternaIndependiente) {
    resuelto.monto_interna = Number(parsed.monto_interna);
  }
  const plameId = plameIdDeConcepto(concepto);
  if (!plameId) return null;
  return {
    plame_configuracion_id: plameId,
    tipo_concepto: tipoSingular(concepto.tipo_concepto || tipoConcepto),
    monto: parseFloat(resuelto.monto || 0),
    destino_ajuste: resuelto.destino_ajuste,
    monto_interna: resuelto.monto_interna != null ? parseFloat(resuelto.monto_interna) : null,
  };
}

/**
 * Payload de recálculo: solo conceptos cambiados / nuevos / restaurados,
 * más IDs PLAME a borrar. No incluye el resto de la plantilla.
 */
export function construirPayloadAjustesRecalculo({
  empleadosConConceptos = [],
  conceptosTemporales = {},
  conceptosEliminados = {},
  empleadosConceptosRestaurados = new Set(),
  defaultDestinoFn = () => 'plame',
} = {}) {
  const ids = new Set([
    ...Object.keys(conceptosTemporales || {}),
    ...Object.keys(conceptosEliminados || {}),
    ...[...((empleadosConceptosRestaurados instanceof Set)
      ? empleadosConceptosRestaurados
      : (empleadosConceptosRestaurados || []))].map(String),
  ]);

  (empleadosConConceptos || []).forEach((emp) => {
    if (empleadoTieneNuevos(emp)) ids.add(String(emp.id));
  });

  const payload = [];

  ids.forEach((empleadoId) => {
    const empleado = (empleadosConConceptos || []).find(
      (emp) => String(emp.id) === String(empleadoId)
    );
    if (!empleado) return;

    const grouped = agruparConceptos(empleado.conceptos);
    const defaultDestino = defaultDestinoFn(empleadoId) || 'plame';
    const temporales = mapaEmpleado(conceptosTemporales, empleadoId) || {};
    const eliminados = mapaEmpleado(conceptosEliminados, empleadoId) || {};
    const restaurado = setTieneEmpleado(empleadosConceptosRestaurados, empleadoId);

    if (restaurado) {
      const todos = flattenConceptos(grouped)
        .filter((c) => buscarValorPorConcepto(eliminados, c) !== true)
        .map((c) => construirFilaAjuste(
          c,
          buscarValorPorConcepto(temporales, c),
          defaultDestino,
          c.tipo_concepto
        ))
        .filter(Boolean);
      payload.push({
        id: Number(empleado.id),
        modo: 'reemplazar',
        conceptos: colapsarPorPlameId(todos).map((c) => normalizarConceptoParaGuardar(c)).filter(Boolean),
        eliminar_plame_ids: [],
      });
      return;
    }

    const cambiar = [];
    const eliminarPlameIds = [];

    flattenConceptos(grouped).forEach((c) => {
      const estaEliminado = buscarValorPorConcepto(eliminados, c) === true;
      if (estaEliminado) {
        const pid = plameIdDeConcepto(c);
        if (pid != null) eliminarPlameIds.push(pid);
        return;
      }
      const rawAjuste = buscarValorPorConcepto(temporales, c);
      if (rawAjuste == null && !c.esNuevo) return;
      const fila = construirFilaAjuste(c, rawAjuste, defaultDestino, c.tipo_concepto);
      if (fila) cambiar.push(fila);
    });

    const eliminarUnicos = [...new Set(eliminarPlameIds)];
    if (cambiar.length === 0 && eliminarUnicos.length === 0) return;

    payload.push({
      id: Number(empleado.id),
      modo: 'parcial',
      conceptos: colapsarPorPlameId(cambiar).map((c) => normalizarConceptoParaGuardar(c)).filter(Boolean),
      eliminar_plame_ids: eliminarUnicos,
    });
  });

  return payload;
}

export function aplicarPayloadAConceptosLocales(conceptosActuales, payloadEmpleado) {
  const grouped = agruparConceptos(conceptosActuales);
  if (!payloadEmpleado) return grouped;

  if (payloadEmpleado.modo === 'reemplazar') {
    const porTipo = { ingresos: [], descuentos: [], aportes: [] };
    (payloadEmpleado.conceptos || []).forEach((c) => {
      const bucket = tipoBucket(c.tipo_concepto);
      const prev = flattenConceptos(grouped).find(
        (x) => String(plameIdDeConcepto(x)) === String(c.plame_configuracion_id)
      );
      porTipo[bucket].push({
        ...(prev || {}),
        ...c,
        esNuevo: false,
      });
    });
    return {
      ingresos: colapsarPorPlameId(porTipo.ingresos),
      descuentos: colapsarPorPlameId(porTipo.descuentos),
      aportes: colapsarPorPlameId(porTipo.aportes),
    };
  }

  const eliminar = new Set((payloadEmpleado.eliminar_plame_ids || []).map(String));
  const next = {
    ingresos: grouped.ingresos.filter((c) => !eliminar.has(String(plameIdDeConcepto(c)))),
    descuentos: grouped.descuentos.filter((c) => !eliminar.has(String(plameIdDeConcepto(c)))),
    aportes: grouped.aportes.filter((c) => !eliminar.has(String(plameIdDeConcepto(c)))),
  };

  (payloadEmpleado.conceptos || []).forEach((c) => {
    const bucket = tipoBucket(c.tipo_concepto);
    const idx = next[bucket].findIndex(
      (x) => String(plameIdDeConcepto(x)) === String(c.plame_configuracion_id)
    );
    if (idx >= 0) {
      next[bucket][idx] = {
        ...next[bucket][idx],
        monto: c.monto,
        destino_ajuste: c.destino_ajuste,
        monto_interna: c.monto_interna,
        esNuevo: false,
        esModificado: true,
      };
    } else {
      next[bucket].push({ ...c, esNuevo: false });
    }
  });

  return {
    ingresos: colapsarPorPlameId(next.ingresos),
    descuentos: colapsarPorPlameId(next.descuentos),
    aportes: colapsarPorPlameId(next.aportes),
  };
}

/**
 * Aplica ajustes al array de cálculo sin reemplazar conceptos no tocados.
 */
export function aplicarAjustesAConceptosUsuario({
  conceptosUsuario = {},
  conceptosAjustes,
  temporales = {},
  eliminados = {},
  defaultDestino = 'plame',
  canalCalculo = null,
} = {}) {
  const patchBucket = (origen, extraAjustes) => {
    const base = Array.isArray(origen) ? [...origen] : [];
    const vistos = new Set(base.map((c) => String(plameIdDeConcepto(c) || codigoDeConcepto(c))));

    const filtrados = base.filter((c) => buscarValorPorConcepto(eliminados, c) !== true);
    const mapeados = filtrados.map((concepto) => {
      const rawAjuste = buscarValorPorConcepto(temporales, concepto);
      if (rawAjuste == null && !concepto.destino_ajuste) return concepto;
      const parsed = parseAjusteTemporal(rawAjuste);
      const destino = rawAjuste != null
        ? getDestinoDesdeAjuste(rawAjuste, concepto.destino_ajuste || defaultDestino)
        : (concepto.destino_ajuste || defaultDestino);
      const montoPlame = rawAjuste != null
        ? getMontoDesdeAjusteParaCanal(rawAjuste, 'plame', concepto.monto)
        : concepto.monto;
      const montoInterna = rawAjuste != null
        ? getMontoDesdeAjusteParaCanal(rawAjuste, 'interna', concepto.monto_interna ?? concepto.monto)
        : (concepto.monto_interna ?? concepto.monto);
      const tieneInterna = parsed
        && (
          (parsed.monto_interna !== undefined && parsed.monto_interna !== null && parsed.monto_interna !== '')
          || parsed.destino === 'interna'
          || parsed.destino === 'ambas'
        );
      const tienePlame = parsed
        && (
          (parsed.monto_plame !== undefined && parsed.monto_plame !== null && parsed.monto_plame !== '')
          || parsed.destino === 'plame'
          || parsed.destino === 'ambas'
          || parsed.destino == null
        );

      if (canalCalculo === 'interna') {
        if (!tieneInterna && rawAjuste != null) return concepto;
        return {
          ...concepto,
          destino_ajuste: 'interna',
          monto_interna: Number(montoInterna),
        };
      }
      if (canalCalculo === 'plame') {
        if (!tienePlame && rawAjuste != null) return concepto;
        return {
          ...concepto,
          monto: montoPlame,
          destino_ajuste: 'plame',
          monto_interna: concepto.monto_interna ?? null,
        };
      }

      if (String(destino).toLowerCase() === 'interna' && !tienePlame) {
        return {
          ...concepto,
          destino_ajuste: destino,
          monto_interna: Number(montoInterna),
        };
      }
      return {
        ...concepto,
        monto: tienePlame ? montoPlame : concepto.monto,
        destino_ajuste: tienePlame && tieneInterna ? 'ambas' : destino,
        monto_interna: tieneInterna
          ? Number(montoInterna)
          : (String(destino).toLowerCase() === 'ambas'
            ? Number(montoPlame)
            : (concepto.monto_interna ?? null)),
      };
    });

    (extraAjustes || []).forEach((c) => {
      if (buscarValorPorConcepto(eliminados, c) === true) return;
      const key = String(plameIdDeConcepto(c) || codigoDeConcepto(c));
      if (!key || vistos.has(key)) return;
      if (!c.esNuevo && buscarValorPorConcepto(temporales, c) == null) return;
      vistos.add(key);
      mapeados.push(c);
    });

    return colapsarPorPlameId(mapeados);
  };

  const ajustes = agruparConceptos(conceptosAjustes);

  return {
    ...conceptosUsuario,
    ingresos: patchBucket(conceptosUsuario.ingresos, ajustes.ingresos),
    descuentos: patchBucket(conceptosUsuario.descuentos, ajustes.descuentos),
    aportes: patchBucket(conceptosUsuario.aportes, ajustes.aportes),
  };
}

export function deduplicarConceptosPorPlame(lista = []) {
  return colapsarPorPlameId(lista);
}

export function empleadoTieneCambiosAjustes({
  empleadoId,
  conceptosTemporales,
  conceptosEliminados,
  empleadosConceptosRestaurados,
  empleado,
} = {}) {
  if (mapaEmpleado(conceptosTemporales, empleadoId)) {
    if (Object.keys(mapaEmpleado(conceptosTemporales, empleadoId)).length > 0) return true;
  }
  if (mapaEmpleado(conceptosEliminados, empleadoId)) {
    if (Object.keys(mapaEmpleado(conceptosEliminados, empleadoId)).length > 0) return true;
  }
  if (setTieneEmpleado(empleadosConceptosRestaurados, empleadoId)) return true;
  return empleadoTieneNuevos(empleado);
}

export default {
  plameIdDeConcepto,
  codigoDeConcepto,
  clavesDeConcepto,
  agruparConceptos,
  colapsarPorPlameId,
  fusionarConceptosFichaConAjustes,
  fusionarConceptosPlameEInternos,
  normalizarConceptoParaGuardar,
  construirPayloadAjustesRecalculo,
  aplicarPayloadAConceptosLocales,
  aplicarAjustesAConceptosUsuario,
  deduplicarConceptosPorPlame,
  empleadoTieneCambiosAjustes,
};
